import React from 'react';
import PropTypes from 'prop-types';
import ReviewRow from './ReviewRow';
import UIHelper from '../../utils/UIHelper';

/**
 * @typedef {import('../../types/Sequences').Sequence} Sequence
 * @typedef {import('../../types/Sequences').SequenceEntry} SequenceEntry
 * @param {Object} props - The component props.
 * @param {Sequence} props.sequence - The sequence data containing review items.
 * @returns {JSX.Element} The rendered table element.
 */
export function ReviewTable({ sequence }) {
  /**
   * Helper to display duration nicely (e.g. 5s)
   * @param {number} duration - duration in milliseconds
   * @returns {string} Formatted duration
   */
  const formatDuration = duration => {
    return UIHelper.time.formatDuration(duration).formatted;
  };

  /**
   * Calculates the total duration of the review items.
   * Preserves original logic: counts images, videos, and playlists (via entries).
   * @param {SequenceEntry[]} entries - The review data items.
   * @returns {number} The total duration in milliseconds.
   */
  const calculateTotalDuration = entries => {
    return entries.reduce((acc, item) => {
      return acc + item.asset.duration;
    }, 0);
  };

  const renderRow = (item, index) => {
    return <ReviewRow key={`row-${index}`} item={item} index={index} />;
  };

  const totalDuration = calculateTotalDuration(sequence.sequenceEntries);

  return (
    <div className="review-table table">
      <table>
        <thead>
          <tr>
            <th>#</th>
            <th>Type</th>
            <th>Thumbnail</th>
            <th>Name</th>
            <th>Duration</th>
            <th>Resolution</th>
            <th>Data</th>
          </tr>
        </thead>
        <tbody>{sequence.sequenceEntries.map((item, index) => renderRow(item, index + 1))}</tbody>
        <tfoot className="review-table-footer">
          <tr>
            <td colSpan={4}>
              <div>
                <b>Total Play Duration:</b>
              </div>
            </td>
            <td>{formatDuration(totalDuration)}</td>
            <td />
          </tr>
        </tfoot>
      </table>
    </div>
  );
}

ReviewTable.propTypes = {
  sequence: PropTypes.object.isRequired,
};
